import React, { useCallback, useEffect, useState } from 'react';
import yup from '../../utility/yup';
import { createStyles, makeStyles } from '@material-ui/styles';
import { Theme } from '@material-ui/core';
import { useField } from 'formik';

import StatusIcon from './StatusIcon';

export const passwordSchema = yup.string()
  .min(7, 'length')
  .max(32, 'length')
  .matches(/[A-Z]/, 'upper')
  .matches(/[a-z]/, 'lower')
  .matches(/[^A-Za-z0-9]/, 'special')
  .matches(/[0-9]/, 'digit')
  .required('required');

const useStyles = makeStyles(({ colors }: Theme) => createStyles({
  root: {},
  item: {},
  icon: {
    width: '14px',
    height: '14px',
    marginRight: '14px',
  },
  passwordTitle: {
    fontSize: '16px',
    fontWeight: 500,
    color: colors.unselected,
    marginTop: '22px',
    marginBottom: '12px',
  },
}), { name: 'PasswordError' });

type Props = {
  name: string;
};

const PasswordError = ({
  name,
}: Props) => {
  const classes = useStyles();
  const [, meta] = useField({ name });
  const [errors, setErrors] = useState<string[]>([]);
  const [hadError, markError] = useState<boolean>(false);

  const updateValidation = useCallback((updatedErrors: string[]) => {
    setErrors(updatedErrors);
    if (updatedErrors.length) {
      markError(true);
    }
  }, [setErrors, markError]);

  useEffect(() => {
    passwordSchema.validate(meta.value, { abortEarly: false })
      .then(
        () => updateValidation([]),
        (e) => updateValidation(e.errors),
      );
  }, [meta.value, setErrors, updateValidation]);

  if (meta.touched && hadError) {
    return (
      <div className={classes.root}>
        <div className={classes.passwordTitle}>Password Requirements:</div>
        <div className={classes.item}>
          <StatusIcon
            state={!errors.includes('length')}
            className={classes.icon}
          />
          Between 7 and 32 characters long
        </div>
        <div className={classes.item}>
          <StatusIcon
            state={!errors.includes('upper')}
            className={classes.icon}
          />
          1 upper case character
        </div>
        <div className={classes.item}>
          <StatusIcon
            state={!errors.includes('lower')}
            className={classes.icon}
          />
          1 lower case character
        </div>
        <div className={classes.item}>
          <StatusIcon
            state={!errors.includes('special')}
            className={classes.icon}
          />
          1 special character
        </div>
        <div className={classes.item}>
          <StatusIcon
            state={!errors.includes('digit')}
            className={classes.icon}
          />
          1 digit
        </div>
      </div>
    );
  }

  return null;
};

export default PasswordError;
